import {
  DateRange,
  ModifierCallable,
  IModifiers,
  BlackoutDay,
  MonthHandler,
  DateModifier,
  ModifierIndex,
  ModifierRange,
  DayData
} from '../types';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import { startOfWeek, endOfWeek } from 'date-fns';
import { formatDate as formatDateInContext } from '../../../components/controls/DatesRangeSelector/commons';

export type CustomDateRange = {
  from: Date | null;
  to: Date | null;
};
export type GeneratedDateFn = (date: Date | null) => void;

export const KEY_BACKSPACE = 8;
export const KEY_TAB = 9;
export const KEY_ENTER = 13;
export const KEY_ESC = 27;
export const KEY_SPACE = 32;
export const KEY_PAGE_UP = 33;
export const KEY_PAGE_DOWN = 34;
export const KEY_END = 35;
export const KEY_HOME = 36;
export const KEY_ARROW_LEFT = 37;
export const KEY_ARROW_UP = 38;
export const KEY_ARROW_RIGHT = 39;
export const KEY_ARROW_DOWN = 40;

/**
 * Default modifiers.
 */
export const defaultModifiers: IModifiers = {
  disabled: false,
  hidden: false,
  interactive: true,
  aftermonth: false,
  beforemonth: false,
  selected: false,
  preSelected: false,
  today: false,
  past: false,
  beginRange: false,
  endRange: false,
  blackout: false
};

export function chunk(arr: any, chunkSize: number = 1, cache: any = []) {
  const tmp = [...arr];
  if (chunkSize <= 0) {
    return cache;
  }
  while (tmp.length) {
    cache.push(tmp.splice(0, chunkSize) as any);
  }
  return cache;
}

/**
 * Given a date generate an array of array of dates. Each array represent a week
 * of month from the current date
 * @param date
 * @param remainingDays
 * @param transformCallback
 */
export function generateMonth<T = Date>(
  date: Date = new Date(),
  remainingDays = false,
  transformCallback: GeneratedDateFn = (date) => date,
  locale: $TSFixMe = {}
): Array<Array<T>> {
  const startDayOfMonth = dayjs(date).startOf('month').toDate();
  // FIXME: start of week
  // const firstDayOfWeek = dayjs(startDayOfMonth).startOf('week').toDate();
  const firstDayOfWeek = startOfWeek(startDayOfMonth, {
    weekStartsOn: locale?._week?.dow
  });
  const startDiff = dayjs(startDayOfMonth).diff(dayjs(firstDayOfWeek), 'day');

  const lastDayOfMonth = dayjs(date).endOf('month').toDate();
  // FIXME: start of week
  // const lastDayOfWeek = dayjs(lastDayOfMonth).startOf('week').toDate();
  const lastDayOfWeek = endOfWeek(lastDayOfMonth, {
    weekStartsOn: locale?._week?.dow
  });
  const endDiff = dayjs(lastDayOfWeek).diff(dayjs(date).endOf('month'), 'day');

  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const daysInMonth = dayjs(date).daysInMonth();
  const startWeekday = startDiff;
  const endWeekday = endDiff;

  return chunk(
    [
      ...Array.from({ length: startWeekday }, (_, i) => {
        const generatedDate = remainingDays
          ? dayjs(firstDay)
              .subtract(startWeekday - i, 'day')
              .toDate()
          : null;
        return transformCallback(generatedDate);
      }),
      ...Array.from({ length: daysInMonth }, (_, i) => {
        const generatedDate = new Date(date.getFullYear(), date.getMonth(), i + 1);
        return transformCallback(generatedDate);
      }),
      ...Array.from({ length: endWeekday }, (_, i) => {
        const generatedDate = remainingDays ? dayjs(firstDay).add(i, 'day').toDate() : null;
        return transformCallback(generatedDate);
      })
    ],
    7
  );
}

export function isDateOutOfView(date: Date, numberOfMonths: number, startDateShowOnCalendar: Date) {
  const endDisplayRange = lastDayOfMonth(
    addMonthsNumber(startDateShowOnCalendar, numberOfMonths - 1)
  );
  const startDisplayRange = getFirstDayOfMonth(startDateShowOnCalendar);
  // console.log('startDisplayRange', startDisplayRange, 'endDisplayRange', endDisplayRange);

  return !isDayInRange(date, { start: startDisplayRange, end: endDisplayRange });
}

export function getFirstDayOfWeek(): number {
  return 0;
}

export function getFirstDayOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, 12);
}

/**
 * Return `true` if a day is in the future, e.g. tomorrow or any day
 * after tomorrow.
 *
 * @export
 * @param  {Date}  date
 * @return {Boolean}
 */
export function isFutureDay(date: Date): boolean {
  const tomorrow = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
  tomorrow.setHours(0, 0, 0, 0);
  return date >= tomorrow;
}

export function getDaysInMonth(date: Date): number {
  const resultDate = getFirstDayOfMonth(date);

  resultDate.setMonth(resultDate.getMonth() + 1);
  resultDate.setDate(resultDate.getDate() - 1);

  return resultDate.getDate();
}

export function isRangeOfDates(value: CustomDateRange): boolean {
  return !!(value && value.from && value.to);
}

export function getMonthsDiff(d1: Date, d2: Date): Number {
  return d2.getMonth() - d1.getMonth() + 12 * (d2.getFullYear() - d1.getFullYear());
}

export function getWeekArray(d: Date, firstDayOfWeek = getFirstDayOfWeek(), fixedWeeks: boolean) {
  const daysInMonth = getDaysInMonth(d);
  const dayArray: Array<Date> = [];

  let week: Array<Date> = [];
  const weekArray: Array<Array<Date>> = [];

  for (let i = 1; i <= daysInMonth; i += 1) {
    dayArray.push(new Date(d.getFullYear(), d.getMonth(), i, 12));
  }

  dayArray.forEach((day) => {
    if (week.length > 0 && day.getDay() === firstDayOfWeek) {
      weekArray.push(week);
      week = [];
    }
    week.push(day);
    if (dayArray.indexOf(day) === dayArray.length - 1) {
      weekArray.push(week);
    }
  });

  // unshift days to start the first week
  const firstWeek = weekArray[0];
  for (let i = 7 - firstWeek.length; i > 0; i -= 1) {
    const outsideDate = clone(firstWeek[0]);
    outsideDate.setDate(firstWeek[0].getDate() - 1);
    firstWeek.unshift(outsideDate);
  }

  // push days until the end of the last week
  const lastWeek = weekArray[weekArray.length - 1];
  for (let i = lastWeek.length; i < 7; i += 1) {
    const outsideDate = clone(lastWeek[lastWeek.length - 1]);
    outsideDate.setDate(lastWeek[lastWeek.length - 1].getDate() + 1);
    lastWeek.push(outsideDate);
  }

  // add extra weeks to reach 6 weeks
  if (fixedWeeks && weekArray.length < 6) {
    let lastExtraWeek;

    for (let i = weekArray.length; i < 6; i += 1) {
      lastExtraWeek = weekArray[weekArray.length - 1];
      const lastDay = lastExtraWeek[lastExtraWeek.length - 1];
      const extraWeek = [];

      for (let j = 0; j < 7; j += 1) {
        const outsideDate = clone(lastDay);
        outsideDate.setDate(lastDay.getDate() + j + 1);
        extraWeek.push(outsideDate);
      }

      weekArray.push(extraWeek);
    }
  }

  return weekArray;
}

export const getAddDays = (date: Date, amount: number) => {
  return dayjs(date).add(amount, 'day').toDate();
};
export const getSubDays = (date: Date, amount: number) => {
  return dayjs(date).subtract(amount, 'day').toDate();
};

export function startOfMonth(d: Date): Date {
  const newDate = clone(d);
  newDate.setDate(1);
  newDate.setHours(12, 0, 0, 0); // always set noon to avoid time zone issues
  return newDate;
}

export function nodeListToArray(nodeList: NodeList) {
  return Array.prototype.slice.call(nodeList, 0);
}

export function hasOwnProp(obj: object, prop: string) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

/**
 * Clone a date object.
 *
 * @export
 * @param  {Date} d The date to clone
 * @return {Date} The cloned date
 */
export function clone(d: Date): Date {
  return new Date(d.getTime());
}

/**
 * Return `true` if the passed value is a valid JavaScript Date object.
 *
 * @export
 * @param {any} value
 * @returns {Boolean}
 */
export function isDate(value: unknown): boolean {
  return value instanceof Date && !isNaN(value.valueOf());
}

/**
 * Return `d` as a new date with `n` months added.
 *
 * @export
 * @param {Date} date
 * @param {number} amount
 */
export const addMonthsNumber = (date: Date, amount: number) => {
  return dayjs(date).add(amount, 'month').toDate();
};

/**
 * Return `true` if two dates are the same day, ignoring the time.
 *
 * @export
 * @param  {Date}  d1
 * @param  {Date}  d2
 * @return {Boolean}
 */
export function isSameDay(d1: Date, d2?: Date): boolean {
  if (!d1 || !d2) {
    return false;
  }
  return (
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  );
}

/**
 * Return `true` if two dates fall in the same month.
 *
 * @export
 * @param  {Date}  d1
 * @param  {Date}  d2
 * @return {Boolean}
 */
export function isSameMonth(d1: Date, d2: Date): boolean {
  if (!d1 || !d2) {
    return false;
  }
  return d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
}

export const subYearsNumber = (date: Date, amount: number) => {
  return dayjs(date).subtract(amount, 'year').toDate();
};

export const getMonthNumber = (date: Date) => {
  return dayjs(date).get('month');
};

export const startDayOfWeek = (date: Date) => {
  return dayjs(date).startOf('week').toDate();
};

export const addYearsNumber = (date: Date, amount: number) => {
  return dayjs(date).add(amount, 'year').toDate();
};

export const endDayOfWeek = (date: Date) => {
  return dayjs(date).endOf('week').toDate();
};

export const subMonthsNumber = (date: Date, amount: number) => {
  return dayjs(date).subtract(amount, 'month').toDate();
};

const changeDateInSameWeek = (
  date: Date,
  isValid: (date: Date) => boolean,
  operation: 'next' | 'previous',
  unit: 'month' | 'year' = 'month'
) => {
  let newDate;
  if (unit === 'month') {
    newDate = addMonthsNumber(date, 1);
    if (operation === 'previous') {
      newDate = subMonthsNumber(date, 1);
    }
  } else {
    //if is year unit
    newDate = addYearsNumber(date, 1);
    if (operation === 'previous') {
      newDate = subYearsNumber(date, 1);
    }
  }
  //generate the months
  const currentMonth = generateMonth(date);
  const newMonth = generateMonth(newDate);

  //normalize the number of weeks of month
  if (currentMonth.length < newMonth.length) {
    while (currentMonth.length < newMonth.length) {
      currentMonth.push(Array.from({ length: 7 }));
    }
  } else {
    while (newMonth.length < currentMonth.length) {
      newMonth.push(Array.from({ length: 7 }));
    }
  }

  //locate current week and day of week
  const indexData = { weekIndex: -1, dayIndex: -1, date: null as any };

  currentMonth.every((week, weekIndex) => {
    const dayOfWeekIndex = week.findIndex((day) => dayjs(date).isSame(dayjs(day), 'day'));

    if (dayOfWeekIndex !== -1) {
      indexData.date = week[dayOfWeekIndex];
      indexData.dayIndex = dayOfWeekIndex;
      indexData.weekIndex = weekIndex;
      return false;
    }
    return true;
  });

  let resultDate = newMonth[indexData.weekIndex][indexData.dayIndex];
  if (!resultDate || !isValid(resultDate)) {
    resultDate = findAvailableDay(newMonth, indexData, isValid);
  }

  return resultDate;
};

export const nextMonthDateInSameDayWeek = (date: Date, isValidDate: (date: Date) => boolean) => {
  let nextDate = addMonthsNumber(date, 1);
  const firstDay = getFirstDayOfMonth(nextDate);
  if (isValidDate(firstDay)) {
    while (!isValidDate(nextDate) && !dayjs(firstDay).isSame(nextDate, 'day')) {
      nextDate = dayjs(nextDate).subtract(1, 'day').toDate();
    }
  }
  return nextDate;
  //TODO: commented until confirmation
  // return changeDateInSameWeek(date, isValidDate, 'next', 'month');
};

export const previousMonthDateInSameDayWeek = (
  date: Date,
  isValidDate: (date: Date) => boolean
) => {
  let previousDate = subMonthsNumber(date, 1);
  const lastDay = lastDayOfMonth(previousDate);
  if (isValidDate(lastDay)) {
    while (!isValidDate(previousDate) && !dayjs(lastDay).isSame(previousDate, 'day')) {
      previousDate = dayjs(previousDate).add(1, 'day').toDate();
    }
  }
  return previousDate;
  //TODO: commented until confirmation
  //   return changeDateInSameWeek(date, isValidDate, 'previous', 'month');
};

export const nextYearDateInSameDayWeek = (date: Date, isValidDate: (date: Date) => boolean) => {
  return changeDateInSameWeek(date, isValidDate, 'next', 'year');
};

export const previousYearDateInSameDayWeek = (date: Date, isValidDate: (date: Date) => boolean) => {
  return changeDateInSameWeek(date, isValidDate, 'previous', 'year');
};

/**
 * Returns `true` if the first day is before the second day.
 *
 * @export
 * @param {Date} d1
 * @param {Date} d2
 * @returns {Boolean}
 */
export function isDayBefore(d1: Date, d2: Date): boolean {
  return dayjs(d1).isBefore(d2, 'day');
  // const day1 = clone(d1).setHours(0, 0, 0, 0);
  // const day2 = clone(d2).setHours(0, 0, 0, 0);
  // return day1 < day2;
}

/**
 * Returns `true` if the first day is after the second day.
 *
 * @export
 * @param {Date} d1
 * @param {Date} d2
 * @returns {boolean}
 */
export function isDayAfter(d1: Date, d2: Date): boolean {
  const day1 = clone(d1).setHours(0, 0, 0, 0);
  const day2 = clone(d2).setHours(0, 0, 0, 0);
  return day1 > day2;
}

/**
 * Return `true` if a day is in the past, e.g. yesterday or any day
 * before yesterday.
 *
 * @export
 * @param  {Date}  d
 * @return {boolean}
 */
export function isPastDay(d: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return isDayBefore(d, today);
}

/**
 * Return `true` if day `d` is between days `d1` and `d2`,
 * without including them.
 *
 * @export
 * @param  {Date}  d
 * @param  {Date}  d1
 * @param  {Date}  d2
 * @param {Boolean} include Determine if the the bounds are included in the condition
 * @return {Boolean}
 */
export function isDayBetween(d: Date, d1: Date, d2: Date, include = false): boolean {
  dayjs.extend(isSameOrAfter);
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isBetween);

  if (include) {
    return (
      dayjs(d).isBetween(dayjs(d1), dayjs(d2), 'day', '[]') ||
      dayjs(d).isBetween(dayjs(d2), dayjs(d1), 'day', '[]')
    );
  }
  return (
    dayjs(d).isBetween(dayjs(d1), dayjs(d2), 'day', '()') ||
    dayjs(d).isBetween(dayjs(d2), dayjs(d1), 'day', '()')
  );
}

/**
 * Return `true` if a day is included in a range of days.
 *
 * @export
 * @param  {Date}  day
 * @param  {Object}  range
 * @return {Boolean}
 */
export function isDayInRange(day: Date, range: DateRange): boolean {
  const { start, end } = range;
  if (!start && !end) {
    return false;
  }

  if (start && end) {
    return isDayBetween(day, start, end, true);
  }
  if (start) {
    return isSameDay(day, start);
  }
  if (end) {
    return isSameDay(day, end);
  }
  return false;
}

export function isSameDate(date1: Date, date2: Date): boolean {
  return isSameDay(date1, date2);
}

/**
 * Return the year's week number (as per ISO, i.e. with the week starting from monday)
 * for the given day.
 *
 * @export
 * @param {Date} day
 * @returns {Number}
 */
export function getWeekNumber(day: Date): number {
  const date = clone(day);
  date.setHours(0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  // @ts-ignore
  return Math.ceil(((date - new Date(date.getFullYear(), 0, 1)) / 8.64e7 + 1) / 7);
}

export function isValidModifier(modifier: IModifiers): boolean {
  return (
    !modifier.disabled &&
    !modifier.past &&
    !modifier.hidden &&
    !modifier.blackout &&
    modifier.interactive
  );
}

export const debounce = (fn: Function, timeout: number) => {
  let timer: number;
  return function () {
    const fnCall = () => fn.apply(null, arguments);

    clearTimeout(timer);
    timer = window.setTimeout(fnCall, timeout);
  };
};

function isPlainObject(value: any): boolean {
  return value.toString() === '[object Object]';
}

/**
 * get modifiers for a specific date
 *
 * @category Eval function
 */
export function getModifiers(
  day: Date | null | undefined,
  componentModifiers: Partial<DateModifier>
): IModifiers {
  const modifiers: IModifiers = { ...defaultModifiers };
  if (!day) {
    modifiers.hidden = true;
    modifiers.interactive = true;
    modifiers.disabled = true;

    return modifiers;
  }

  Object.keys(componentModifiers).forEach((key: string) => {
    const modifierValue = componentModifiers[key as ModifierIndex];

    // const isFunction = !('after' in modifierValue);
    const isFunction = modifierValue instanceof Function;
    const isObject = isPlainObject(modifierValue);

    //if the key exist in the modifiers
    if (key in modifiers && modifierValue) {
      if (isFunction) {
        modifiers[key as ModifierIndex] = (modifierValue as ModifierCallable)(day);
      } else if (isObject) {
        const { before, after } = modifierValue as ModifierRange;
        modifiers[key as keyof IModifiers] = isDayBefore(day, before) || isDayAfter(day, after);
      }
    }
  });

  modifiers.interactive = !modifiers.disabled && !modifiers.blackout;

  return modifiers;
}

export const transformBlackoutDaysToDate = (
  blackoutDays: Array<BlackoutDay>,
  format: string,
  context: any = {}
): Array<Required<DateRange>> => {
  return blackoutDays.map((item) => {
    if (typeof item === 'string') {
      const date = parseDateFromString(item, format, context);
      return { start: date, end: date };
    } else {
      const startDate = parseDateFromString(item.start, format, context);
      const endDate = parseDateFromString(item.end, format, context);
      return { start: startDate, end: endDate };
    }
  });
};

export const parseDateFromString = (date: string, format: string, context: any): Date => {
  const parsedDate = dayjs(date).toDate();
  const dateFormatted = formatDate(parsedDate, format, context);
  const isCorrectFormat = dateFormatted === date;
  if (isCorrectFormat) {
    return parsedDate;
  }
  throw new Error(`Invalid date: ${date}, must have this format ${format}`);
};

export const weeksOfMonth = (date: Date) => {
  dayjs.extend(weekOfYear);
  const firstDate = dayjs(date).startOf('month');
  const lastDate = dayjs(date).endOf('month');
  const days = Math.ceil(lastDate.diff(firstDate, 'day', true));
  const dayofWeek = firstDate.day();
  return Math.ceil((days + dayofWeek - 1) / 7);
};

//Aux methods
/**
 * Find the nearest available day in the same month
 * @param monthData
 * @param indexData
 * @param isValid
 */
const findAvailableDay = (
  monthData: Array<Array<Date>>,
  indexData: { weekIndex: number; dayIndex: number; date: Date },
  isValid: Function
) => {
  let { weekIndex } = indexData;

  let direction: 'down' | 'up' = 'up';
  if (weekIndex === 0) {
    direction = 'down';
  }

  let resultDate = monthData[weekIndex][indexData.dayIndex];
  if (direction === 'down') {
    for (let i = weekIndex; i < monthData.length; i++) {
      resultDate = monthData[i][indexData.dayIndex];
      if (resultDate && isValid(resultDate)) {
        break;
      }
    }
  } else {
    for (let i = monthData.length - 1; i >= 0; i--) {
      resultDate = monthData[i][indexData.dayIndex];
      if (resultDate && isValid(resultDate)) {
        break;
      }
    }
  }

  if (!resultDate || !isValid(resultDate)) {
    //find the closes available day from begining
    for (let w = 0; w < monthData.length; w++) {
      for (let d = 0; d < monthData[w].length; d++) {
        resultDate = monthData[w][d];
        if (resultDate && isValid(resultDate)) {
          break;
        }
      }
    }
  }
  return resultDate;
};

export const isValidDay = (months: Array<MonthHandler>) => (date: Date): boolean => {
  const data = getDayData(date, months);
  if (data?.modifiers) {
    return isValidModifier(data.modifiers);
  }
  return false;
};

export function getAvailableDay(
  date: Date,
  amount: number,
  isValid: ModifierCallable,
  direction: 'next' | 'previous'
): Date {
  const threshold = 100;
  let counter = 0;
  let changedDate = clone(date);
  let found = false;

  while (counter++ < threshold && !found) {
    if (direction === 'next') {
      changedDate = dayjs(changedDate).add(amount, 'day').toDate();
    } else {
      changedDate = dayjs(changedDate).subtract(amount, 'day').toDate();
    }
    found = isValid(changedDate);
    if (found) {
      break;
    }
  }
  if (!found) {
    return date;
  }
  return changedDate;
}

export const dayInMonth = (day: Date, monthDate: Date) => {
  const firstDay = getFirstDayOfMonth(monthDate);
  const lastDay = lastDayOfMonth(monthDate);
  return isDayInRange(day, { start: firstDay, end: lastDay });
};

export const lastDayOfMonth = (date: Date) => {
  return dayjs(date).endOf('month').toDate();
};

export const isValidDate = (date: Date, modifiers: any) => {
  const dateModifier = getModifiers(date, modifiers);

  return (
    !dateModifier.disabled &&
    dateModifier.interactive &&
    !dateModifier.blackout &&
    !dateModifier.past
  );
};
export const validateDate = (date: Date, modifiers: IModifiers) => {
  return !modifiers.disabled && modifiers.interactive && !modifiers.blackout && !modifiers.past;
};

interface generateMonthListArgument {
  displayDate: Date;
  modifiers: any;
  modifiersColors: any;
  blackOutDays: Array<DateRange>;
  numberOfMonths: number;
  showRemainingDays: boolean;
  locale: $TSFixMe;
}

export const generateMonthList = ({
  displayDate,
  modifiers,
  modifiersColors,
  numberOfMonths = 2,
  showRemainingDays = false,
  locale = {}
}: generateMonthListArgument) => {
  const months: Array<MonthHandler> = [];
  for (let i = 0; i < numberOfMonths; i++) {
    const monthDate = dayjs(displayDate).add(i, 'month').toDate();
    months.push(new MonthHandler(monthDate, modifiers, modifiersColors, showRemainingDays, locale));
  }
  return months;
};

export const generateDataAttributes = (modifier: IModifiers) => {
  return Object.fromEntries(
    Object.entries(modifier)
      .filter(([_state, value]) => Boolean(value))
      .map(([state]) => [`data-fr-${state.toLowerCase()}-day`, ''])
  );
};

export function formatDate(date: Date | DateRange | string, format: string, context = {}): string {
  return formatDateInContext(date, format, context);
}

export const monthInRange = (date: Date, range: DateRange): boolean => {
  dayjs.extend(isBetween);
  if (range.start && range.end) {
    return dayjs(date).isBetween(range.start, range.end, 'month', '[]');
  }
  return false;
};

export function getDayData(date: Date, months: Array<MonthHandler>): DayData | null {
  if (date && months.length > 0) {
    let data = null;
    months.every((month) => {
      data = month.findDay(date);
      return !data;
    });
    return data;
  }
  return null;
}

export function reorderWeekdayDependentLanguage(localizationData: $TSFixMe) {
  const {
    _week = {
      dow: 0
    }
  } = localizationData;
  const weekdays = [0, 1, 2, 3, 4, 5, 6];
  const resp = weekdays.slice(_week.dow, weekdays.length);
  const rest = weekdays.slice(0, _week.dow);
  return [...resp, ...rest];
}
